/**
 * The sentences an employee's own attendance register says about a month.
 *
 * The month itself — which day is present, absent, on leave, a rest day — is
 * already decided once in `utils/attendanceMonth`, and this deliberately does
 * not decide it again. What lives here is the layer above: how many of those
 * days were *meant* to be worked, what the header claims, and whether the
 * month is carrying a problem the employee is the only person who can fix.
 *
 * It is separate from the screen because these are the parts that can be wrong
 * in a way nobody notices — a denominator that quietly counts Sundays, an
 * average taken over days that had no punch — and a wrong number on somebody's
 * own attendance record is worse than no number.
 */

import { STATUS } from "../../../../../utils/attendanceMonth";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Explicit, not toLocaleDateString: ICU renders September as "Sept", which is
// not what any other month does and reads as a typo next to "August".
export const monthName = (month) => MONTHS[month] || "";

/** Days that are neither a rest day nor a holiday — the ones owed work. */
const isWorkingDay = (day) =>
  !day.isWeeklyOff && day.status.key !== STATUS.HOLIDAY.key;

/**
 * The register's denominator.
 *
 * Counts the whole month, not only the part that has happened: "24 of 25
 * working days" is a statement about March, and a denominator that grew every
 * morning would make the same month read differently depending on when it was
 * opened. Leave is included — a day taken as leave was still a working day that
 * the employee was rostered for.
 */
export const workingDayCount = (days = []) => days.filter(isWorkingDay).length;

/**
 * Days that count as attended. Late is present: someone who arrived at 09:40
 * came to work, and a register that says otherwise is picking a fight the
 * attendance policy already settled.
 */
export const attendedCount = (days = []) =>
  days.filter(
    (d) => d.status.key === STATUS.PRESENT.key || d.status.key === STATUS.LATE.key
  ).length;

/** "24 present of 25 working days" — the header's whole claim. */
export const presenceLine = (days = []) => {
  const worked = workingDayCount(days);
  if (!worked) return "No working days this month";
  return `${attendedCount(days)} present of ${worked} working day${worked === 1 ? "" : "s"}`;
};

const pad2 = (n) => String(n).padStart(2, "0");

/** Minutes since midnight as "09:00". */
export const clockLabel = (mins) =>
  mins == null || Number.isNaN(mins) ? null : `${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}`;

/**
 * "General day shift, 09:00–18:00".
 *
 * Reads the times off the first day that actually states any, rather than
 * `days[0]` — that is Sunday, and on most rosters it is the rest day carrying
 * either no times or an unconfigured 00:00–00:00. Either would put a shift
 * nobody works under somebody's heading. A zero-length span is treated as
 * unset for the same reason: no real shift starts and ends at the same minute.
 */
export const shiftLine = (shift) => {
  if (!shift?.name) return null;
  const working = (shift.days || []).find(
    (d) =>
      d && !d.isHoliday && d.startTime != null && d.endTime != null && d.startTime !== d.endTime
  );
  const start = clockLabel(working?.startTime);
  const end = clockLabel(working?.endTime);
  return start && end ? `${shift.name}, ${start}–${end}` : shift.name;
};

/**
 * Hours worked, and what the average is taken across.
 *
 * The divisor is days that actually recorded time, not working days: averaging
 * across a day the employee was on approved leave would report them as slower
 * than they were, and the number is meant to be recognisable to the person it
 * describes.
 */
export const hoursSummary = (days = []) => {
  const withTime = days.filter((d) => (d.totalHours || 0) > 0);
  const minutes = withTime.reduce((sum, d) => sum + (d.totalHours || 0), 0);
  const hours = minutes / 60;
  return {
    hours,
    days: withTime.length,
    label: hours ? hours.toFixed(1) : "—",
    sub: withTime.length
      ? `${(hours / withTime.length).toFixed(1)} average across ${withTime.length} day${
          withTime.length === 1 ? "" : "s"
        }`
      : "no time recorded yet",
  };
};

/**
 * The one thing on this screen only the employee can resolve.
 *
 * Deliberately says less than the design book does. The book's version ends
 * "or they will be unpaid", and that is a claim about what payroll does with an
 * uncorrected day — a rule this product does not currently encode anywhere.
 * Telling somebody their pay is at risk on the strength of a sentence in a
 * design document would be the worst kind of wrong, so the note stops at what
 * the register can actually prove: the day is not counted as present.
 */
export const ordinal = (n) => {
  // 11th, 12th and 13th are the exceptions every naive rule gets wrong
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
};

export const missingPunchNote = (days = []) => {
  const open = days.filter((d) => d.status.key === STATUS.MISSING_PUNCH.key);
  if (!open.length) return null;
  const one = open.length === 1;
  const dates = open.map((d) => ordinal(d.dayNum));
  const list = one ? dates[0] : `${dates.slice(0, -1).join(", ")} and ${dates[dates.length - 1]}`;
  return {
    count: open.length,
    days: open,
    lead: one ? "One day is missing a punch." : `${open.length} days are missing a punch.`,
    body:
      `The ${list} ${one ? "has" : "have"} no check-out recorded, so ${one ? "it is" : "they are"} ` +
      `not counted as present. Fixing ${one ? "it" : "them"} files a correction for your manager to approve.`,
  };
};

/**
 * What the employee can do about a single day, and nothing more.
 *
 * A missing punch is theirs to correct. An absence is theirs to explain — the
 * same regularization record, but the day needs an account rather than a time.
 * Everything else is either fine or not an attendance problem at all: a rest
 * day, a holiday and approved leave are all correct as they stand, and offering
 * a "fix" on them would invite somebody to file a request that means nothing.
 */
export const dayAction = (day) => {
  if (!day || day.isFuture) return null;
  if (day.status.key === STATUS.MISSING_PUNCH.key) return "Fix";
  if (day.status.key === STATUS.ABSENT.key) return "Explain";
  return null;
};
